import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessagesSquare, Search, Sparkles, User, Trash2, ChevronDown,
  AlertCircle, Loader2, Users, Coins, Activity,
} from 'lucide-react';

type Source = { id: string; title: string; sourceUrl: string; sourceType?: string };
type Turn = {
  id: string; role: 'user' | 'assistant'; content: string;
  mode: string | null; model: string | null;
  sources: { rag: Source[]; web: Source[] } | null;
  inputTokens: number | null; outputTokens: number | null;
  searches: number | null; estCost: number | null;
  error: string | null; createdAt: string;
};
type ListItem = {
  id: string; userId: string | null; userLabel: string; title: string;
  createdAt: string; updatedAt: string; turnCount: number;
  modes: string[]; estCost: number; hasError: boolean; preview: string;
};
type Transcript = { id: string; userLabel: string; title: string; createdAt: string; estCost: number; turns: Turn[] };
type Stats = { conversations: number; turns: number; estCost: number; users: number; last7: number };
type UserFilter = { userId: string | null; label: string; conversations: number };

const MODE_LABEL: Record<string, string> = { internal: 'Internal', search: 'Search', deep: 'Deep' };

const when = (d: string) => new Date(d).toLocaleString('en-CA', {
  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
});
const relative = (d: string) => {
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  const days = Math.round(mins / (60 * 24));
  return days < 30 ? `${days}d ago` : new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};
const initials = (label: string) => label.trim().slice(0, 2).toUpperCase() || '??';

const CARD = 'bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px]';
const BADGE = 'inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-[7px] py-[2px] rounded-full border';

const StatTile: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className={`${CARD} p-4 flex items-center gap-3`}>
    <div className="w-9 h-9 rounded-[10px] bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center shrink-0 text-[color:var(--accent)]">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-[19px] font-semibold text-[color:var(--text-primary)] leading-tight">{value}</div>
      <div className="text-[11px] text-[color:var(--text-muted)] truncate">{label}</div>
    </div>
  </div>
);

// One message in the transcript. User turns read as sent messages; assistant turns
// mirror the live Chat page (markdown + linked citations) with an admin-only
// footer showing which mode/model answered and what it cost.
const TurnView: React.FC<{ turn: Turn; index: number }> = ({ turn, index }) => {
  const [showSources, setShowSources] = useState(false);
  const sources = [...(turn.sources?.rag ?? []), ...(turn.sources?.web ?? [])];

  const citationMap = useMemo(() => {
    const m: Record<string, string> = {};
    sources.forEach(s => { m[s.id] = s.sourceUrl; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn.id]);

  const withCitations = (text: string): React.ReactNode =>
    text.split(/(\[(?:RAG|WEB)-(?:\d+|NAI)\])/).map((part, i) => {
      const m = part.match(/\[(RAG|WEB)-(\d+|NAI)\]/);
      const url = m && citationMap[`${m[1]}-${m[2]}`];
      return url
        ? <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-[color:var(--accent-strong)] text-[11px] font-mono">{part}</a>
        : <span key={i}>{part}</span>;
    });

  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%]">
          <div className="bg-[var(--accent)] rounded-[18px] px-4 py-3 text-[14px] text-white leading-[1.6] whitespace-pre-wrap">
            {turn.content}
          </div>
          <div className="text-[10px] text-[color:var(--text-muted)] mt-1 text-right">{when(turn.createdAt)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-[14px]">
      <div className="w-7 h-7 rounded-lg bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center shrink-0 mt-[2px]">
        <Sparkles size={13} className="text-[color:var(--accent)]" />
      </div>
      <div className="flex-1 min-w-0">
        {turn.error ? (
          <div className="flex items-start gap-2 text-[13px] text-[color:var(--danger-strong)] bg-[var(--danger-dim)] border border-[var(--danger-dim)] rounded-[12px] px-3 py-2">
            <AlertCircle size={14} className="mt-[2px] shrink-0" />
            <span>This turn failed: <span className="font-mono text-[12px]">{turn.error}</span></span>
          </div>
        ) : (
          <div className="text-[14px] leading-[1.7] text-[color:var(--text-secondary)]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="mb-3 leading-[1.7]">{React.Children.map(children, c => typeof c === 'string' ? withCitations(c) : c)}</p>,
                li: ({ children }) => <li className="mb-1">{React.Children.map(children, c => typeof c === 'string' ? withCitations(c) : c)}</li>,
                h3: ({ children }) => <h3 className="text-[14px] font-semibold text-[color:var(--text-primary)] mt-4 mb-2">{children}</h3>,
                code: ({ children }) => <code className="bg-[var(--surface)] text-[color:var(--accent-strong)] px-[6px] py-[1px] rounded-[4px] text-[12px] font-mono">{children}</code>,
                strong: ({ children }) => <strong className="text-[color:var(--text-primary)] font-semibold">{children}</strong>,
                ul: ({ children }) => <ul className="pl-5 mb-3 list-disc">{children}</ul>,
                ol: ({ children }) => <ol className="pl-5 mb-3 list-decimal">{children}</ol>,
                a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-[color:var(--accent-strong)] underline">{children}</a>,
              }}
            >
              {turn.content}
            </ReactMarkdown>
          </div>
        )}

        {sources.length > 0 && (
          <div>
            <button
              onClick={() => setShowSources(v => !v)}
              className="flex items-center gap-[6px] text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)] bg-transparent border-none pt-1 pb-1 cursor-pointer transition-colors"
            >
              <ChevronDown size={12} className={`transition-transform duration-150 ${showSources ? 'rotate-180' : ''}`} />
              {sources.length} source{sources.length === 1 ? '' : 's'}
            </button>
            {showSources && (
              <div className="mt-1 pl-[14px] border-l-2 border-[var(--border)] flex flex-col gap-[2px]">
                {sources.map(s => (
                  <a key={s.id} href={s.sourceUrl} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--accent-strong)] no-underline px-2 py-1 rounded-md transition-colors">
                    <span className="font-mono text-[color:var(--accent-strong)]">[{s.id}]</span> {s.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin-only telemetry for this answer */}
        <div className="flex items-center gap-2 flex-wrap mt-2 text-[10px] text-[color:var(--text-muted)]">
          <span>{when(turn.createdAt)}</span>
          {turn.mode && <span className={`${BADGE} border-[var(--accent-border)] bg-[var(--accent-dim)] text-[color:var(--accent-strong)]`}>{MODE_LABEL[turn.mode] || turn.mode}</span>}
          {turn.model && <span className="font-mono">{turn.model}</span>}
          {turn.inputTokens != null && <span>{turn.inputTokens.toLocaleString()} in / {(turn.outputTokens ?? 0).toLocaleString()} out</span>}
          {!!turn.searches && <span>{turn.searches} web search{turn.searches === 1 ? '' : 'es'}</span>}
          {turn.estCost != null && <span>${turn.estCost.toFixed(3)}</span>}
          <span className="sr-only">turn {index + 1}</span>
        </div>
      </div>
    </div>
  );
};

const ChatHistory: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserFilter[]>([]);
  const [list, setList] = useState<ListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<Transcript | null>(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadStats = useCallback(() => {
    axios.get('/api/conversations/stats').then(r => setStats(r.data)).catch(() => {});
    axios.get('/api/conversations/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const loadList = useCallback((skip = 0) => {
    setLoadingList(true);
    const params: Record<string, string | number> = { skip };
    if (debouncedQ.trim()) params.q = debouncedQ.trim();
    if (userId) params.userId = userId;
    axios.get('/api/conversations', { params })
      .then(r => {
        setTotal(r.data.total);
        setHasMore(r.data.hasMore);
        setList(prev => (skip === 0 ? r.data.conversations : [...prev, ...r.data.conversations]));
      })
      .catch(() => {})
      .finally(() => setLoadingList(false));
  }, [debouncedQ, userId]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(0); }, [loadList]);

  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    setLoadingActive(true);
    axios.get(`/api/conversations/${activeId}`)
      .then(r => setActive(r.data))
      .catch(() => setActive(null))
      .finally(() => setLoadingActive(false));
  }, [activeId]);

  const remove = (id: string) => {
    if (!window.confirm('Delete this conversation and its full transcript? This cannot be undone.')) return;
    axios.delete(`/api/conversations/${id}`)
      .then(() => {
        setList(prev => prev.filter(c => c.id !== id));
        setTotal(t => Math.max(0, t - 1));
        if (activeId === id) setActiveId(null);
        loadStats();
      })
      .catch(() => {});
  };

  const field = 'bg-[var(--surface)] border border-[var(--border)] rounded-[10px] px-3 py-[9px] text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none focus:border-[var(--accent)] transition-colors';

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1">Chat History</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">
          Every conversation users have had with the assistant, with the sources and cost behind each answer.
        </p>
      </div>

      {/* Headline numbers */}
      <div className="grid gap-3 mb-6 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
        <StatTile icon={<MessagesSquare size={17} />} label="Conversations" value={stats ? stats.conversations.toLocaleString() : '—'} />
        <StatTile icon={<Activity size={17} />} label="Messages" value={stats ? stats.turns.toLocaleString() : '—'} />
        <StatTile icon={<Users size={17} />} label="People chatting" value={stats ? stats.users.toLocaleString() : '—'} />
        <StatTile icon={<Sparkles size={17} />} label="Active last 7 days" value={stats ? stats.last7.toLocaleString() : '—'} />
        <StatTile icon={<Coins size={17} />} label="Estimated spend" value={stats ? `$${stats.estCost.toFixed(2)}` : '—'} />
      </div>

      <div className="grid gap-6 [grid-template-columns:minmax(0,1fr)] lg:[grid-template-columns:minmax(320px,400px)_minmax(0,1fr)]">
        {/* Left: filters + conversation list */}
        <div className="min-w-0">
          <div className={`${CARD} p-4 mb-4`}>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search questions, answers, people…"
                className={`${field} w-full pl-9`}
              />
            </div>
            <select value={userId} onChange={e => setUserId(e.target.value)} className={`${field} w-full`}>
              <option value="">All users</option>
              {users.map(u => (
                <option key={u.userId ?? 'unknown'} value={u.userId ?? ''}>
                  {u.label} ({u.conversations})
                </option>
              ))}
            </select>
            <div className="text-[11px] text-[color:var(--text-muted)] mt-2">
              {loadingList && list.length === 0 ? 'Loading…' : `${total.toLocaleString()} conversation${total === 1 ? '' : 's'}`}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {!loadingList && list.length === 0 && (
              <div className={`${CARD} p-8 text-center`}>
                <MessagesSquare size={24} className="mx-auto mb-2 text-[color:var(--text-muted)]" />
                <div className="text-[13px] text-[color:var(--text-primary)] mb-1">No conversations yet</div>
                <p className="text-[12px] text-[color:var(--text-muted)] m-0">
                  {debouncedQ || userId ? 'Nothing matches this filter.' : 'Chats are recorded from the moment a user sends their first message.'}
                </p>
              </div>
            )}

            {list.map(c => {
              const selected = c.id === activeId;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  className={`${CARD} w-full text-left p-4 cursor-pointer transition-all duration-150 hover:border-[var(--accent-border)] ${selected ? 'border-[var(--accent-border)] bg-[var(--accent-dim)]' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-center shrink-0 text-[11px] font-semibold text-[color:var(--text-secondary)]">
                      {initials(c.userLabel)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-[3px]">
                        <span className="text-[12px] text-[color:var(--text-secondary)] truncate">{c.userLabel}</span>
                        <span className="text-[11px] text-[color:var(--text-muted)] shrink-0">· {relative(c.updatedAt)}</span>
                      </div>
                      <div className="text-[13px] text-[color:var(--text-primary)] leading-[1.45] line-clamp-2">{c.title}</div>
                      {c.preview && <div className="text-[11px] text-[color:var(--text-muted)] mt-1 line-clamp-2 leading-[1.45]">{c.preview}</div>}
                      <div className="flex items-center gap-[6px] flex-wrap mt-2">
                        <span className={`${BADGE} border-[var(--border)] bg-[var(--surface)] text-[color:var(--text-muted)]`}>
                          {c.turnCount} msg{c.turnCount === 1 ? '' : 's'}
                        </span>
                        {c.modes.map(m => (
                          <span key={m} className={`${BADGE} border-[var(--accent-border)] bg-[var(--accent-dim)] text-[color:var(--accent-strong)]`}>
                            {MODE_LABEL[m] || m}
                          </span>
                        ))}
                        {c.estCost > 0 && (
                          <span className={`${BADGE} border-[var(--border)] bg-[var(--surface)] text-[color:var(--text-muted)]`}>
                            ${c.estCost.toFixed(2)}
                          </span>
                        )}
                        {c.hasError && (
                          <span className={`${BADGE} border-[var(--danger-dim)] bg-[var(--danger-dim)] text-[color:var(--danger-strong)]`}>
                            <AlertCircle size={9} /> error
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {hasMore && (
              <button
                onClick={() => loadList(list.length)}
                disabled={loadingList}
                className="text-[12px] text-[color:var(--text-secondary)] bg-[var(--surface)] border border-[var(--border)] rounded-[10px] py-2 hover:border-[var(--accent-border)] hover:text-[color:var(--text-primary)] disabled:opacity-60 transition-colors"
              >
                {loadingList ? 'Loading…' : `Load more (${total - list.length} left)`}
              </button>
            )}
          </div>
        </div>

        {/* Right: transcript */}
        <div className="min-w-0">
          {!activeId && (
            <div className={`${CARD} p-12 text-center`}>
              <MessagesSquare size={30} className="mx-auto mb-3 text-[color:var(--text-muted)]" />
              <div className="text-[14px] text-[color:var(--text-primary)] mb-1">Pick a conversation</div>
              <p className="text-[12px] text-[color:var(--text-muted)] m-0">Select a thread on the left to read the full transcript.</p>
            </div>
          )}

          {activeId && loadingActive && (
            <div className={`${CARD} p-12 flex items-center justify-center gap-2 text-[13px] text-[color:var(--text-muted)]`}>
              <Loader2 size={16} className="animate-spin" /> Loading transcript…
            </div>
          )}

          {activeId && !loadingActive && active && (
            <div className={`${CARD} overflow-hidden`}>
              <div className="flex items-start gap-3 p-5 border-b border-[var(--glass-border)]">
                <div className="w-9 h-9 rounded-full bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center shrink-0 text-[12px] font-semibold text-[color:var(--accent-strong)]">
                  {initials(active.userLabel)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold text-[color:var(--text-primary)] leading-snug">{active.title}</div>
                  <div className="text-[11px] text-[color:var(--text-muted)] mt-[3px] flex items-center gap-[6px] flex-wrap">
                    <User size={11} /> {active.userLabel}
                    <span>· started {when(active.createdAt)}</span>
                    <span>· {active.turns.length} message{active.turns.length === 1 ? '' : 's'}</span>
                    {active.estCost > 0 && <span>· ${active.estCost.toFixed(3)} est.</span>}
                  </div>
                </div>
                <button
                  onClick={() => remove(active.id)}
                  title="Delete conversation"
                  className="text-[color:var(--text-muted)] hover:text-[color:var(--danger-strong)] shrink-0 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>

              <div className="p-5 flex flex-col gap-5 max-h-[calc(100vh-320px)] overflow-y-auto">
                {active.turns.map((t, i) => <TurnView key={t.id} turn={t} index={i} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;
